import { IProject } from '../entities/IProject';
import { ITriggerActionGroup } from '../entities/ITriggerActionGroup';
import { ITriggerAction } from '../entities/ITriggerAction';
import { Log } from '@fanfanlo';

const fileLog = new Log(false, 'projectMerge');
fileLog.pause = true;

/**
 * 合并策略配置
 */
export enum MergeStrategy {
  /** 方案1：直接替换 */
  REPLACE = 'replace',
  /** 方案2：智能合并（推荐） */
  MERGE = 'merge'
}

/**
 * 获取当前使用的合并策略
 * 可通过环境变量或配置切换
 */
export function getMergeStrategy(): MergeStrategy {
  const fnLog = fileLog.sub(false, 'getMergeStrategy_fn');
  fnLog.pause = true;

  // 从环境变量或全局配置获取策略，默认使用 MERGE
  const strategy = (typeof window !== 'undefined' && (window as any).__PROJECT_MERGE_STRATEGY__)
    || process.env.NEXT_PUBLIC_PROJECT_MERGE_STRATEGY
    || MergeStrategy.MERGE;

  fnLog.log(`[getMergeStrategy] 当前策略: ${strategy}`);
  return strategy as MergeStrategy;
}

/**
 * 方案1：直接替换
 * 后端返回的 project 直接替换本地 project
 */
export function replaceProject(localProject: IProject, backendProject: IProject): void {
  const fnLog = fileLog.sub(false, 'replaceProject_fn');
  fnLog.pause = true;

  fnLog.log(`[replaceProject] 开始替换 project, ulid=${localProject.ulid}`);
  fnLog.log(`[replaceProject] 后端 project uuid=${backendProject.uuid}`);

  // 直接替换所有字段
  Object.assign(localProject, backendProject);

  fnLog.log(`[replaceProject] 替换完成, 新 uuid=${localProject.uuid}`);
}

/**
 * 方案2：智能合并（推荐）
 * 保留本地修改，同步后端新增字段
 */
export function mergeProject(localProject: IProject, backendProject: IProject): void {
  const fnLog = fileLog.sub(false, 'mergeProject_fn');
  fnLog.pause = true;

  fnLog.log(`[mergeProject] 开始合并 project`);
  fnLog.log(`[mergeProject] 本地 ulid=${localProject.ulid}, 后端 ulid=${backendProject.ulid}`);
  fnLog.log(`[mergeProject] 本地 uuid=${localProject.uuid}, 后端 uuid=${backendProject.uuid}`);

  // 1. 合并后端字段（这些字段以后端为准）
  mergeBackendFields(localProject, backendProject, fnLog);

  // 2. 合并业务字段（全量替换）
  mergeBusinessFields(localProject, backendProject, fnLog);

  // 3. 递归合并 children
  mergeChildren(localProject, backendProject, fnLog);

  fnLog.log(`[mergeProject] 合并完成`);
}

/**
 * 合并后端字段
 * 这些字段以后端为准，直接覆盖
 */
function mergeBackendFields(localProject: IProject, backendProject: IProject, parentLog: Log): void {
  const fnLog = parentLog.sub(false, 'mergeBackendFields_fn');
  fnLog.pause = true;

  fnLog.log(`[mergeBackendFields] 开始合并后端字段`);

  // 后端字段列表
  const backendFields = [
    'uuid',
    'list',
    'createdAt',
    'updatedAt',
    'deletedAt',
    'ownerUuid',
    'authorUuid',
    'currentVersion',
    'childrenVersion'
  ];

  backendFields.forEach(field => {
    const oldValue = (localProject as any)[field];
    const newValue = (backendProject as any)[field];

    if (oldValue !== newValue) {
      fnLog.log(`[mergeBackendFields] 更新字段 ${field}: ${JSON.stringify(oldValue)} -> ${JSON.stringify(newValue)}`);
      (localProject as any)[field] = newValue;
    }
  });

  fnLog.log(`[mergeBackendFields] 后端字段合并完成`);
}

/**
 * 合并业务字段
 * 全量替换
 */
function mergeBusinessFields(localProject: IProject, backendProject: IProject, parentLog: Log): void {
  const fnLog = parentLog.sub(false, 'mergeBusinessFields_fn');
  fnLog.pause = true;

  fnLog.log(`[mergeBusinessFields] 开始合并业务字段`);

  // 业务字段列表
  const businessFields = [
    'title',
    'subtitle',
    'name',
    'type',
    'category',
    'keepAlive',
    'tags',
    'categories',
    'r2Path',
    'r2Account',
    'usageCount',
    'forkCount',
    'visibility',
    'originUuid',
    'forkFromVersion',
    'isSequential',
    'checks',
    'javaData',
    'webConfig',
    'sourceLang',
    'summaryPath',
    'descriptionPath',
    'briefDescriptionPath',
    'thumbnailPath'
  ];

  businessFields.forEach(field => {
    const oldValue = (localProject as any)[field];
    const newValue = (backendProject as any)[field];

    if (oldValue !== newValue) {
      fnLog.log(`[mergeBusinessFields] 更新字段 ${field}: ${JSON.stringify(oldValue)} -> ${JSON.stringify(newValue)}`);
      (localProject as any)[field] = newValue;
    }
  });

  fnLog.log(`[mergeBusinessFields] 业务字段合并完成`);
}

/**
 * 递归合并 children
 * 根据 ulid 比较判断是否同一对象
 * 如果 ulid 相同且在同一索引位置，则递归合并
 * 否则直接替换
 */
function mergeChildren(localProject: IProject, backendProject: IProject, parentLog: Log): void {
  const fnLog = parentLog.sub(false, 'mergeChildren_fn');
  fnLog.pause = true;

  fnLog.log(`[mergeChildren] 开始合并 children`);
  fnLog.log(`[mergeChildren] 本地 children 数量=${localProject.children?.length || 0}`);
  fnLog.log(`[mergeChildren] 后端 children 数量=${backendProject.children?.length || 0}`);

  // 如果后端没有 children，清空本地 children
  if (!backendProject.children || backendProject.children.length === 0) {
    fnLog.log(`[mergeChildren] 后端没有 children，清空本地 children`);
    localProject.children = [];
    return;
  }

  // 初始化本地 children 数组
  if (!localProject.children) {
    localProject.children = [];
  }

  // 遍历后端 children
  for (let i = 0; i < backendProject.children.length; i++) {
    const backendChild = backendProject.children[i];
    const localChild = localProject.children[i];

    fnLog.log(`[mergeChildren] 处理 child[${i}], 后端 ulid=${backendChild.ulid}`);

    // 检查是否是同一个对象（通过 ulid 比较）
    if (localChild && localChild.ulid === backendChild.ulid) {
      fnLog.log(`[mergeChildren] child[${i}] ulid 相同，执行递归合并`);
      mergeChild(localChild, backendChild, fnLog);
    } else {
      fnLog.log(`[mergeChildren] child[${i}] ulid 不同或本地不存在，直接替换`);
      if (localChild) {
        fnLog.log(`[mergeChildren] 本地 ulid=${localChild.ulid}, 后端 ulid=${backendChild.ulid}`);
      }
      localProject.children[i] = backendChild;
    }
  }

  // 如果本地 children 更多，删除多余的
  if (localProject.children.length > backendProject.children.length) {
    fnLog.log(`[mergeChildren] 本地 children 更多，删除多余的 (${localProject.children.length} -> ${backendProject.children.length})`);
    localProject.children.length = backendProject.children.length;
  }

  fnLog.log(`[mergeChildren] children 合并完成`);
}

/**
 * 递归合并单个 child（TriggerActionGroup）
 */
function mergeChild(localChild: ITriggerActionGroup, backendChild: ITriggerActionGroup, parentLog: Log): void {
  const fnLog = parentLog.sub(false, 'mergeChild_fn');
  fnLog.pause = true;

  fnLog.log(`[mergeChild] 开始合并 child, ulid=${localChild.ulid}`);

  // 1. 合并后端字段
  const backendFields = [
    'uuid',
    'list',
    'createdAt',
    'updatedAt',
    'deletedAt',
    'ownerUuid',
    'authorUuid',
    'currentVersion',
    'childrenVersion'
  ];

  backendFields.forEach(field => {
    const oldValue = (localChild as any)[field];
    const newValue = (backendChild as any)[field];

    if (oldValue !== newValue) {
      fnLog.log(`[mergeChild] 更新字段 ${field}: ${JSON.stringify(oldValue)} -> ${JSON.stringify(newValue)}`);
      (localChild as any)[field] = newValue;
    }
  });

  // 2. 合并业务字段
  const businessFields = [
    'name',
    'isSequential',
    'checks',
    'javaData',
    'webConfig',
    'sourceLang',
    'summaryPath',
    'descriptionPath',
    'briefDescriptionPath',
    'visibility',
    'originUuid',
    'forkFromVersion'
  ];

  businessFields.forEach(field => {
    const oldValue = (localChild as any)[field];
    const newValue = (backendChild as any)[field];

    if (oldValue !== newValue) {
      fnLog.log(`[mergeChild] 更新字段 ${field}: ${JSON.stringify(oldValue)} -> ${JSON.stringify(newValue)}`);
      (localChild as any)[field] = newValue;
    }
  });

  // 3. 递归合并 children
  if (!backendChild.children || backendChild.children.length === 0) {
    fnLog.log(`[mergeChild] 后端没有 children，清空本地 children`);
    localChild.children = [];
  } else {
    if (!localChild.children) {
      localChild.children = [];
    }

    for (let i = 0; i < backendChild.children.length; i++) {
      const backendGrandChild = backendChild.children[i];
      const localGrandChild = localChild.children[i];

      fnLog.log(`[mergeChild] 处理 grandChild[${i}], 后端 ulid=${backendGrandChild.ulid}`);

      if (localGrandChild && localGrandChild.ulid === backendGrandChild.ulid) {
        fnLog.log(`[mergeChild] grandChild[${i}] ulid 相同，执行递归合并`);
        mergeTriggerAction(localGrandChild, backendGrandChild, fnLog);
      } else {
        fnLog.log(`[mergeChild] grandChild[${i}] ulid 不同或本地不存在，直接替换`);
        localChild.children[i] = backendGrandChild;
      }
    }

    if (localChild.children.length > backendChild.children.length) {
      fnLog.log(`[mergeChild] 本地 children 更多，删除多余的`);
      localChild.children.length = backendChild.children.length;
    }
  }

  fnLog.log(`[mergeChild] child 合并完成`);
}

/**
 * 合并 TriggerAction
 * 将后端的 TriggerAction 数据合并到本地 TriggerAction
 */
function mergeTriggerAction(localTriggerAction: ITriggerAction, backendTriggerAction: ITriggerAction, parentLog: Log): void {
  const fnLog = parentLog.sub(false, 'mergeTriggerAction_fn');
  fnLog.pause = true;

  fnLog.log(`[mergeTriggerAction] 开始合并 triggerAction, ulid=${localTriggerAction.ulid}`);

  // 合并后端字段
  const backendFields = [
    'uuid',
    'triggerGroupUuid',
    'triggerGroupUlid',
    'actionGroupUuid',
    'actionGroupUlid',
    'createdAt',
    'updatedAt',
    'deletedAt',
    'ownerUuid',
    'authorUuid',
    'currentVersion'
  ];

  backendFields.forEach(field => {
    const oldValue = (localTriggerAction as any)[field];
    const newValue = (backendTriggerAction as any)[field];

    if (oldValue !== newValue) {
      fnLog.log(`[mergeTriggerAction] 更新字段 ${field}: ${JSON.stringify(oldValue)} -> ${JSON.stringify(newValue)}`);
      (localTriggerAction as any)[field] = newValue;
    }
  });

  fnLog.log(`[mergeTriggerAction] triggerAction 合并完成`);
}

/**
 * 根据策略合并或替换 project
 */
export function syncProject(localProject: IProject, backendProject: IProject): void {
  const fnLog = fileLog.sub(false, 'syncProject_fn');
  fnLog.pause = true;

  const strategy = getMergeStrategy();
  fnLog.log(`[syncProject] 使用策略: ${strategy}`);
  fnLog.log(`[syncProject] 本地 ulid=${localProject.ulid}, 后端 ulid=${backendProject.ulid}`);

  if (strategy === MergeStrategy.REPLACE) {
    fnLog.log(`[syncProject] 执行直接替换`);
    replaceProject(localProject, backendProject);
  } else {
    fnLog.log(`[syncProject] 执行智能合并`);
    mergeProject(localProject, backendProject);
  }

  fnLog.log(`[syncProject] 同步完成`);
}
